<template>
  <div class="content-page">
    <h1 class="page-title">{{ pageTitle }}</h1>
    
    <div class="content-grid">
      <div v-for="item in contentItems" :key="item.id" class="content-item">
        <music-card 
          :image="IMAGE_BASE_URL + item.poster" 
          :title="item.zhName" 
          :id="item.id"
          @card-click="handleCardClick(item.id)"
        />
      </div>
    </div>
    
    <div class="loading-more" v-if="loading">
      <div class="loading-spinner"></div>
      <span>加载中...</span>
    </div>
    
    <div class="loading-more" v-if="noMoreData && !showLoginPrompt">
      <span>加载完毕，没有更多数据了</span>
    </div>

    <!-- 未授权提示 -->
    <div class="login-prompt" v-if="showLoginPrompt">
      <p>登录后可以享受更多内容</p>
      <button class="login-btn" @click="showLoginModal">登录</button>
    </div>
  </div>
</template>

<script>
import MusicCard from '../components/MusicCard.vue';
import { getCategoryList } from '../api';
import { IMAGE_BASE_URL } from '../utils/constants';

export default {
  name: 'ContentPage',
  components: {
    MusicCard
  },
  props: {
    categoryId: {
      type: Number,
      default: 0
    },
    pageType: {
      type: String,
      default: 'all',
      validator: (value) => ['mv', 'singer', 'all'].includes(value)
    },
    isLoggedIn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      contentItems: [],
      page: 0,
      IMAGE_BASE_URL,
      throttleTimer: null,
      noMoreData: false,
      showLoginPrompt: false,
      isUserLoggedIn: false
    }
  },
  computed: {
    pageTitle() {
      switch(this.pageType) {
        case 'mv': return 'MV';
        case 'singer': return '歌手';
        case 'all': return '全部';
        default: return '内容';
      }
    }
  },
  created() {
    // 如果已登录，设置状态
    const token = localStorage.getItem('token');
    if (token) {
      this.isUserLoggedIn = true;
    } else {
      // 检查是否之前遇到过未授权错误
      const hasUnauthorized = localStorage.getItem('hasUnauthorizedError');
      if (hasUnauthorized === 'true') {
        this.showLoginPrompt = true;
      }
    }
    
    // 加载内容数据
    this.loadData();
  },
  mounted() {
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll);
  },
  watch: {
    // 当分类ID或页面类型变化时，重新加载数据
    categoryId() {
      this.page = 0;
      this.contentItems = [];
      this.showLoginPrompt = false;
      this.loadData();
    },
    pageType() {
      this.page = 0;
      this.contentItems = [];
      this.showLoginPrompt = false;
      this.loadData();
    },
    isLoggedIn(newValue) {
      console.log('登录状态变化:', newValue);
      if (newValue) {
        // 用户登录后，重置状态并重新加载数据
        this.page = 0;
        this.contentItems = [];
        this.showLoginPrompt = false;
        this.noMoreData = false;
        
        // 清除未授权错误标志
        localStorage.removeItem('hasUnauthorizedError');
        
        // 延迟一点执行，确保token已保存
        setTimeout(() => {
          this.loadData();
        }, 500);
      }
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        if (this.pageType === 'all') {
          await this.loadAllData();
        } else {
          const categoryId = this.categoryId || (this.pageType === 'mv' ? 80 : 53);
          const response = await getCategoryList(this.page, categoryId);
          
          if (response && response.data) {
            const newItems = response.data.data || [];

            // 检查是否有新数据
            if (newItems.length === 0) {
              this.noMoreData = true;
            } else {
              if (this.page === 0) {
                this.contentItems = newItems;
              } else {
                this.contentItems = [...this.contentItems, ...newItems];
              }
            }
          } else if (response && response.rc === 3998 && response.msg === "未授权") {
            console.log('检测到未授权错误：', response);
            this.showLoginPrompt = true;
            this.noMoreData = true;
            // 记录未授权状态
            if (!this.isLoggedIn) {
              localStorage.setItem('hasUnauthorizedError', 'true');
            }
          } else {
            this.noMoreData = true;
          }
        }
      } catch (error) {
        console.error(`获取${this.pageTitle}数据失败:`, error);
        this.noMoreData = true;

        // 处理未授权错误 - 更新处理方式
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.rc === 3998 && errorData.msg === "未授权") {
            console.log('从错误响应中检测到未授权：', errorData);
            this.showLoginPrompt = true;
            // 记录未授权状态
            if (!this.isLoggedIn) {
              localStorage.setItem('hasUnauthorizedError', 'true');
            }
          }
        } else if (error.message && error.message.includes('未授权')) {
          // 直接从错误消息判断
          console.log('从错误消息中检测到未授权');
          this.showLoginPrompt = true;
          // 记录未授权状态
          if (!this.isLoggedIn) {
            localStorage.setItem('hasUnauthorizedError', 'true');
          }
        }
      } finally {
        this.loading = false;
      }
    },
    async loadAllData() {
      try {
        // 获取MV数据
        const mvResponse = await getCategoryList(this.page, 80);
        // 获取歌手数据
        const singerResponse = await getCategoryList(this.page, 53);
        
        let mvData = [];
        let singerData = [];
        // let unauthorized = false;
        
        // 检查是否有未授权错误 - 安全检查每个响应
        if ((mvResponse && mvResponse.rc === 3998 && mvResponse.msg === "未授权") ||
            (singerResponse && singerResponse.rc === 3998 && singerResponse.msg === "未授权")) {
          // 确定哪个响应包含未授权错误
          const unauthorizedResponse = 
            (mvResponse && mvResponse.rc === 3998) ? mvResponse : 
            (singerResponse && singerResponse.rc === 3998) ? singerResponse : null;
            
          console.log('检测到未授权错误 (loadAllData):', unauthorizedResponse);
          // unauthorized = true;
          this.showLoginPrompt = true;
          this.noMoreData = true;
          
          // 记录未授权状态
          if (!this.isLoggedIn) {
            localStorage.setItem('hasUnauthorizedError', 'true');
          }
          return; // 如果未授权，直接返回，不再处理数据
        }
        
        // 安全地处理数据
        if (mvResponse && mvResponse.data) {
          mvData = mvResponse.data.data || [];
        }
        
        if (singerResponse && singerResponse.data) {
          singerData = singerResponse.data.data || [];
        }
        
        // 合并数据
        const newItems = [...mvData, ...singerData];
        
        // 检查是否有新数据
        if (newItems.length === 0) {
          this.noMoreData = true;
        } else {
          if (this.page === 0) {
            this.contentItems = newItems;
          } else {
            this.contentItems = [...this.contentItems, ...newItems];
          }
        }
      } catch (error) {
        console.error('获取全部数据失败:', error);
        this.noMoreData = true;

        // 处理未授权错误 - 更新处理方式
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.rc === 3998 && errorData.msg === "未授权") {
            console.log('从错误响应中检测到未授权 (loadAllData):', errorData);
            this.showLoginPrompt = true;
            // 记录未授权状态
            if (!this.isLoggedIn) {
              localStorage.setItem('hasUnauthorizedError', 'true');
            }
          }
        } else if (error.message && error.message.includes('未授权')) {
          // 直接从错误消息判断
          console.log('从错误消息中检测到未授权 (loadAllData)');
          this.showLoginPrompt = true;
          // 记录未授权状态
          if (!this.isLoggedIn) {
            localStorage.setItem('hasUnauthorizedError', 'true');
          }
        }
      }
    },
    loadMore() {
      // 如果已经没有更多数据，则不再加载
      if (this.noMoreData) {
        // 如果是因为未授权而停止加载，确保登录提示显示
        if (!this.isLoggedIn) {
          this.showLoginPrompt = true;
        }
        return;
      }
      
      this.page++;
      this.loadData();
    },
    handleCardClick(id) {
      // 根据页面类型决定播放视频还是音频
      if (this.pageType === 'singer') {
        this.playAudio(id);
      } else {
        this.playVideo(id);
      }
    },
    playVideo(id) {
      // 通知App组件播放视频
      this.$emit('play-video', id);
    },
    playAudio(id) {
      // 通知App组件播放音频
      this.$emit('play-audio', id);
    },
    handleScroll() {
      try {
        // 已经在加载中，不再触发加载更多
        if (this.loading) return;

        // 添加节流，避免频繁触发
        if (this.throttleTimer) return;
        
        this.throttleTimer = setTimeout(() => {
          try {
            // 获取页面高度、滚动位置和视窗高度
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            
            // 判断是否滚动到距离底部100px以内
            if (scrollTop + windowHeight >= documentHeight - 100) {
              // 如果没有登录且之前有未授权错误，直接显示登录提示
              if (!this.isLoggedIn && this.page > 0) {
                this.showLoginPrompt = true;
              }
              this.loadMore();
            }
          } catch (scrollError) {
            console.error('滚动处理出错:', scrollError);
          } finally {
            // 清除节流计时器
            this.throttleTimer = null;
          }
        }, 200);
      } catch (error) {
        console.error('handleScroll方法出错:', error);
        this.throttleTimer = null;
      }
    },
    showLoginModal() {
      // 设置未授权标志，确保用户看到登录提示
      if (!this.isLoggedIn) {
        localStorage.setItem('hasUnauthorizedError', 'true');
      }
      
      // 触发App组件显示登录弹窗
      this.$emit('show-login');
    }
  }
}
</script>

<style scoped>
.content-page {
  padding: 20px;
  padding-bottom: 40px;
}

.page-title {
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #333;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
}

.loading-more {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  color: #666;
  font-size: 16px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(65, 105, 225, 0.3);
  border-radius: 50%;
  border-top-color: #4169e1;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

/* 登录提示样式 */
.login-prompt {
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #eaeaea;
}

.login-prompt p {
  margin-bottom: 15px;
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.login-btn {
  background: linear-gradient(90deg, #4169e1, #7b68ee);
  color: white;
  border: none;
  padding: 10px 30px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.login-btn:hover {
  background: linear-gradient(90deg, #3158d0, #6a57dd);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(65, 105, 225, 0.3);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 992px) {
  .content-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style> 